import { useEffect, useState } from 'react';
import {
    Box, Typography, TextField, Button, Switch, FormControlLabel, IconButton,
    Paper, Divider, Alert, CircularProgress, Tooltip, Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    getClapsleySettings, saveClapsleySettings,
    sendLeaderboardNow, sendClapsleyTestMessage,
} from '../../api/clapsleyApi';

const DEFAULT_NEW_SALE_TEMPLATE =
`👏 Congrats {agent_name} for new case registration!
📍 {kwp}KWP - {city},{state} - {deposit_date}
Keep it up & let's close more deals!🌊 Towards No.1`;

const DEFAULT_LEADERBOARD_TEMPLATE =
`🏆 *Top 5 Agents — {month}* (month-to-date)

{leaderboard}

Keep pushing! 🌊 Towards No.1`;

const DEFAULT_NOTICE_TEMPLATE =
`{date} - {title}

Check app for more

🌊 Towards No.1`;

const DEFAULT_ONBOARDING_TEMPLATE =
`halo Janny, here's our new member on-boarding!
{name} - {phone}
Upline: {upline} - {uplinephone}
Team: {team}

Thankyou o`;

const DEFAULT_WELCOME_MEMBER_TEMPLATE =
`Hi {name},
Welcome to nexwave team, we are heading towards No.1 in solar industry in Malaysia.

Here's your sales platform:
1. www.sales.nexwaveintl.com
2. you may check tutorial here.

Thanks! Happy selling.
Towards No.1`;

const PP = "'Poppins', sans-serif";

// Recipient row editor — a list of strings, each editable, with add/remove.
function RecipientList({ label, value, onChange, helperText }) {
    const items = Array.isArray(value) ? value : [];
    const updateAt = (i, v) => onChange(items.map((x, idx) => idx === i ? v : x));
    const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, '']);
    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                {label}
            </Typography>
            {helperText && (
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    {helperText}
                </Typography>
            )}
            {items.length === 0 && (
                <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontStyle: 'italic', fontFamily: PP, mb: 1 }}>
                    (empty — will fall back to Default Group JID)
                </Typography>
            )}
            {items.map((v, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField
                        size="small" fullWidth value={v}
                        onChange={(e) => updateAt(i, e.target.value)}
                        placeholder="120363xxxxxxxxxxxxx@g.us  or  60xxxxxxxxx@s.whatsapp.net  or  +60xxxxxxxxx"
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }}
                    />
                    <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeAt(i)} sx={{ color: '#94A3B8' }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ))}
            <Button
                size="small" startIcon={<AddIcon />} onClick={add}
                sx={{ fontFamily: PP, textTransform: 'none', color: '#1A8CFF' }}
            >
                Add recipient
            </Button>
        </Box>
    );
}

export default function WhatsappSettings() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ type: '', msg: '' });
    const [testText, setTestText] = useState('Test from Nexwave admin panel');
    const [testRecipientsCsv, setTestRecipientsCsv] = useState('');
    const [testing, setTesting] = useState(false);
    const [firing, setFiring] = useState(false);

    useEffect(() => {
        getClapsleySettings()
            .then(r => setCfg(r.data))
            .catch(err => setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        setSnack({ type: '', msg: '' });
        try {
            const r = await saveClapsleySettings(cfg);
            setCfg(r.data.cfg);
            setSnack({ type: 'success', msg: 'Settings saved. Changes apply on next message send.' });
        } catch (err) {
            setSnack({ type: 'error', msg: err?.response?.data?.message || err.message });
        } finally {
            setSaving(false);
        }
    };

    const fireLeaderboard = async () => {
        setFiring(true);
        setSnack({ type: '', msg: '' });
        try {
            const r = await sendLeaderboardNow();
            const d = r.data || {};
            if (d.sent)        setSnack({ type: 'success', msg: `Leaderboard sent. ${d.saleCount} sale(s) this month.` });
            else if (d.saleCount === 0) setSnack({ type: 'info', msg: 'No sales this month — nothing sent.' });
            else               setSnack({ type: 'warning', msg: d.note || 'Send failed — check backend logs.' });
        } catch (err) {
            setSnack({ type: 'error', msg: err?.response?.data?.message || err.message });
        } finally {
            setFiring(false);
        }
    };

    const fireTest = async () => {
        setTesting(true);
        setSnack({ type: '', msg: '' });
        try {
            const recipients = testRecipientsCsv.split(',').map(s => s.trim()).filter(Boolean);
            const r = await sendClapsleyTestMessage(testText, recipients);
            const d = r.data || {};
            setSnack({
                type: d.sent ? 'success' : 'warning',
                msg: d.sent
                    ? `Sent to ${(d.recipients || []).join(', ')}.`
                    : (d.note || 'Send failed.'),
            });
        } catch (err) {
            setSnack({ type: 'error', msg: err?.response?.data?.message || err.message });
        } finally {
            setTesting(false);
        }
    };

    if (loading) return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
    );
    if (!cfg) return <Alert severity="error" sx={{ m: 4 }}>{snack.msg || 'Failed to load settings.'}</Alert>;

    return (
        <Box sx={{ maxWidth: 880, mx: 'auto', p: { xs: 2, sm: 3 } }}>
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                    WhatsApp (Clapsley) Settings
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: PP, mt: 0.5 }}>
                    Control which WhatsApp group(s) / personal numbers receive automated messages.
                    Changes take effect on the next send — no restart needed.
                </Typography>
                <Chip label="Super Admin only" size="small" sx={{ mt: 1, background: '#FEF3C7', color: '#92400E', fontFamily: PP, fontWeight: 600 }} />
            </Box>

            {snack.msg && (
                <Alert severity={snack.type || 'info'} sx={{ mb: 2, fontFamily: PP }} onClose={() => setSnack({ type: '', msg: '' })}>
                    {snack.msg}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 2 }}>
                    Triggers
                </Typography>
                <FormControlLabel
                    control={<Switch checked={!!cfg.enableNewSale} onChange={e => setCfg({ ...cfg, enableNewSale: e.target.checked })} />}
                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.88rem' }}>Send congrats on every new sale</Typography>}
                />
                <br />
                <FormControlLabel
                    control={<Switch checked={!!cfg.enableWeeklyLeaderboard} onChange={e => setCfg({ ...cfg, enableWeeklyLeaderboard: e.target.checked })} />}
                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.88rem' }}>Send monthly leaderboard every Sunday 20:00 MYT</Typography>}
                />
                <br />
                <FormControlLabel
                    control={<Switch checked={!!cfg.enableNotice} onChange={e => setCfg({ ...cfg, enableNotice: e.target.checked })} />}
                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.88rem' }}>Send to WhatsApp when a noticeboard announcement is posted</Typography>}
                />
                <br />
                <FormControlLabel
                    control={<Switch checked={!!cfg.enableOnboarding} onChange={e => setCfg({ ...cfg, enableOnboarding: e.target.checked })} />}
                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.88rem' }}>DM the on-boarding coordinator (Janny) when a new user is approved</Typography>}
                />
                <br />
                <FormControlLabel
                    control={<Switch checked={!!cfg.enableWelcomeMember} onChange={e => setCfg({ ...cfg, enableWelcomeMember: e.target.checked })} />}
                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.88rem' }}>DM the new member directly (to their own WhatsApp) when approved</Typography>}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 2 }}>
                    Default Group JID
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Used as the fallback recipient when an event's list below is empty. Find via{' '}
                    <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>
                        curl https://clapsley.com/api/v1/wa/groups -H "X-API-Key: ..."
                    </code>
                </Typography>
                <TextField
                    size="small" fullWidth value={cfg.groupJid || ''}
                    onChange={e => setCfg({ ...cfg, groupJid: e.target.value })}
                    placeholder="120363xxxxxxxxxxxxx@g.us"
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 2 }}>
                    Recipients per event
                </Typography>
                <RecipientList
                    label="🎉 New-sale congrats — send to:"
                    helperText="Each line: a group JID (...@g.us), a personal JID (...@s.whatsapp.net), or a plain phone number."
                    value={cfg.newSaleRecipients}
                    onChange={v => setCfg({ ...cfg, newSaleRecipients: v })}
                />
                <Divider sx={{ my: 2 }} />
                <RecipientList
                    label="🏆 Sunday leaderboard — send to:"
                    helperText="Same format as above. Empty list falls back to the Default Group JID."
                    value={cfg.leaderboardRecipients}
                    onChange={v => setCfg({ ...cfg, leaderboardRecipients: v })}
                />
                <Divider sx={{ my: 2 }} />
                <RecipientList
                    label="📢 Noticeboard announcement — send to:"
                    helperText="Same format as above. Empty list falls back to the Default Group JID."
                    value={cfg.noticeRecipients}
                    onChange={v => setCfg({ ...cfg, noticeRecipients: v })}
                />
                <Divider sx={{ my: 2 }} />
                <RecipientList
                    label="🙋 New-member on-boarding — send to:"
                    helperText="Defaults to Janny (601172699618@s.whatsapp.net). Each line: a personal JID or plain phone."
                    value={cfg.onboardingRecipients}
                    onChange={v => setCfg({ ...cfg, onboardingRecipients: v })}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                    🎉 New-sale congrats template
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Variables: <code>{'{agent_name}'}</code> · <code>{'{cobroke_name}'}</code> · <code>{'{kwp}'}</code> · <code>{'{city}'}</code> · <code>{'{state}'}</code> · <code>{'{deposit_date}'}</code> · <code>{'{customer}'}</code> · <code>{'{price}'}</code>
                    <br/>
                    <code>{'{agent_name}'}</code> is LAST + FIRST (e.g. TAN JIN SHENG) and auto-appends the co-broker (&nbsp;<code>{'A & B'}</code>&nbsp;) when there is one. Lines that end up containing only punctuation (e.g. when city/state are empty) are auto-dropped.
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={4} value={cfg.newSaleTemplate || ''}
                    onChange={e => setCfg({ ...cfg, newSaleTemplate: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }}
                />
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setCfg({ ...cfg, newSaleTemplate: DEFAULT_NEW_SALE_TEMPLATE })}
                    sx={{ mt: 1, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                    Reset to default
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                    🏆 Sunday leaderboard template
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Variables: <code>{'{month}'}</code> (e.g. "May 2026") · <code>{'{leaderboard}'}</code> (the 5 medal rows, auto-built) · <code>{'{agent_count}'}</code>
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={6} value={cfg.leaderboardTemplate || ''}
                    onChange={e => setCfg({ ...cfg, leaderboardTemplate: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }}
                />
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setCfg({ ...cfg, leaderboardTemplate: DEFAULT_LEADERBOARD_TEMPLATE })}
                    sx={{ mt: 1, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                    Reset to default
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                    📢 Noticeboard announcement template
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Variables: <code>{'{title}'}</code> · <code>{'{content}'}</code> (HTML stripped, max 800 chars) · <code>{'{author}'}</code> · <code>{'{date}'}</code>
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={5} value={cfg.noticeTemplate || ''}
                    onChange={e => setCfg({ ...cfg, noticeTemplate: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }}
                />
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setCfg({ ...cfg, noticeTemplate: DEFAULT_NOTICE_TEMPLATE })}
                    sx={{ mt: 1, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                    Reset to default
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                    🙋 New-member on-boarding template (DM to Janny)
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Variables: <code>{'{name}'}</code> (LAST + FIRST, e.g. TAN JIN SHENG) · <code>{'{phone}'}</code> · <code>{'{upline}'}</code> · <code>{'{uplinephone}'}</code> · <code>{'{team}'}</code>
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={4} value={cfg.onboardingTemplate || ''}
                    onChange={e => setCfg({ ...cfg, onboardingTemplate: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }}
                />
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setCfg({ ...cfg, onboardingTemplate: DEFAULT_ONBOARDING_TEMPLATE })}
                    sx={{ mt: 1, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                    Reset to default
                </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 0.5 }}>
                    👋 Welcome DM to the new member
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mb: 1 }}>
                    Sent to the new member's own WhatsApp (their <code>PhoneNumber</code>, normalised to 60xxx). Variables: <code>{'{name}'}</code> (LAST + FIRST)
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={8} value={cfg.welcomeMemberTemplate || ''}
                    onChange={e => setCfg({ ...cfg, welcomeMemberTemplate: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }}
                />
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setCfg({ ...cfg, welcomeMemberTemplate: DEFAULT_WELCOME_MEMBER_TEMPLATE })}
                    sx={{ mt: 1, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                    Reset to default
                </Button>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}
                    sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                        '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } }}
                >
                    {saving ? 'Saving…' : 'Save settings'}
                </Button>
            </Box>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none', background: '#F8FAFC' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 2 }}>
                    Test send
                </Typography>
                <TextField
                    size="small" fullWidth multiline minRows={2} value={testText}
                    onChange={e => setTestText(e.target.value)}
                    label="Test message"
                    sx={{ mb: 1.5 }}
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }}
                    InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.85rem' } }}
                />
                <TextField
                    size="small" fullWidth value={testRecipientsCsv}
                    onChange={e => setTestRecipientsCsv(e.target.value)}
                    label="Recipients (comma-separated) — empty = use Default Group JID"
                    placeholder="120363...@g.us, 60xxxxxxxxx@s.whatsapp.net"
                    sx={{ mb: 1.5 }}
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }}
                    InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.85rem' } }}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined" startIcon={<SendIcon />} onClick={fireTest} disabled={testing}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}
                    >
                        {testing ? 'Sending…' : 'Send test message'}
                    </Button>
                    <Button
                        variant="outlined" startIcon={<EmojiEventsIcon />} onClick={fireLeaderboard} disabled={firing}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: '#7C3AED', borderColor: '#7C3AED' }}
                    >
                        {firing ? 'Firing…' : 'Fire leaderboard NOW'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
